import React, { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { listShipments, createShipment, deleteShipment } from 'services/shipments.service'
import StatusBadge from 'components/StatusBadge'
import TrialBanner, { UpgradeDialog } from 'components/TrialBanner'
import useUsage from 'hooks/useUsage'
import { errorCode } from 'services/auth.service'
import { INCOTERMS, TRANSPORT_MODES } from 'config'
import { useI18n, apiError } from 'i18n'

const STATUS_FILTERS = ['', 'draft', 'review', 'calculated', 'closed']

const PAGE_SIZES = [10, 20, 30, 50]

const inputClass = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none'

const NewShipmentForm = ({ onCreated, onCancel, onLimitReached }) => {
  const { t } = useI18n()
  const [form, setForm] = useState({ reference: '', incoterm: '', transportMode: '', mode: 'actual', usdRate: '' })
  const [busy, setBusy] = useState(false)
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const onSubmit = async (event) => {
    event.preventDefault()
    setBusy(true)
    try {
      const data = { reference: form.reference.trim(), mode: form.mode }
      if (form.incoterm) data.incoterm = form.incoterm
      if (form.transportMode) data.transportMode = form.transportMode
      if (form.usdRate) data.exchangeRates = { USD: Number(form.usdRate) }
      const { shipment } = await createShipment(data)
      toast.success(t('shipments.created'))
      onCreated(shipment)
    } catch (error) {
      if (errorCode(error) === 'TRIAL_LIMIT_REACHED') {
        onLimitReached(error.response.data.usage)
        return
      }
      toast.error(apiError(error, t('shipments.createFailed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className='grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-6'>
      <label className='flex flex-col gap-1 text-xs font-medium text-slate-500 md:col-span-2'>
        {t('shipments.reference')}
        <input required value={form.reference} onChange={set('reference')} placeholder={t('shipments.referencePlaceholder')} className={inputClass} />
      </label>
      <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
        {t('shipments.incoterm')}
        <select value={form.incoterm} onChange={set('incoterm')} className={inputClass}>
          <option value=''>{t('shipments.fromInvoice')}</option>
          {INCOTERMS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </label>
      <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
        {t('shipments.transport')}
        <select value={form.transportMode} onChange={set('transportMode')} className={inputClass}>
          <option value=''>—</option>
          {TRANSPORT_MODES.map((m) => <option key={m} value={m}>{t(`transport.${m}`)}</option>)}
        </select>
      </label>
      <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
        {t('shipments.mode')}
        <select value={form.mode} onChange={set('mode')} className={inputClass}>
          <option value='actual'>{t('modes.actualLong')}</option>
          <option value='estimate'>{t('modes.estimateLong')}</option>
        </select>
      </label>
      <label className='flex flex-col gap-1 text-xs font-medium text-slate-500'>
        {t('shipments.usdRate')}
        <input type='number' step='0.0001' min='0' value={form.usdRate} onChange={set('usdRate')} placeholder={t('shipments.usdRatePlaceholder')} className={inputClass} />
      </label>
      <div className='flex items-center justify-end gap-3 md:col-span-6'>
        <button type='button' onClick={onCancel} className='text-sm font-semibold text-slate-500 hover:underline'>{t('common.cancel')}</button>
        <button type='submit' disabled={busy} className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
          {busy ? t('shipments.creating') : t('shipments.create')}
        </button>
      </div>
    </form>
  )
}

const ShipmentsList = () => {
  const { t, fmt } = useI18n()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [shipments, setShipments] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  // `?new=1` (the dashboard's first-run CTA) lands with the form already open.
  const [creating, setCreating] = useState(searchParams.get('new') === '1')
  const { usage, setUsage } = useUsage()
  const [limitOpen, setLimitOpen] = useState(false)

  const closeLimit = useCallback(() => setLimitOpen(false), [])
  const onNew = () => (usage?.reached ? setLimitOpen(true) : setCreating(true))
  const onLimitReached = (latest) => {
    if (latest) setUsage(latest)
    setCreating(false)
    setLimitOpen(true)
  }

  useEffect(() => {
    if (searchParams.has('new')) setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  // Usage arrives after the form opened from `?new=1`.
  useEffect(() => {
    if (!creating || !usage?.reached) return
    setCreating(false)
    setLimitOpen(true)
  }, [creating, usage])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, pageSize }
      if (status) params.status = status
      if (q) params.q = q
      const data = await listShipments(params)
      setShipments(data.shipments || [])
      setTotal(data.total || 0)
    } catch (error) {
      toast.error(t('shipments.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, status, q])

  useEffect(() => { load() }, [load])

  const changeStatus = (v) => { setStatus(v); setPage(1) }
  const changeQ = (v) => { setQ(v); setPage(1) }
  const changePageSize = (v) => { setPageSize(v); setPage(1) }

  const onDelete = async (s) => {
    if (!window.confirm(t('shipments.confirmDelete', { reference: s.reference }))) return
    try {
      await deleteShipment(s.shipmentId)
      toast.success(t('shipments.deleted'))
      load()
    } catch (error) {
      toast.error(apiError(error, t('shipments.deleteFailed')))
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold'>{t('shipments.title')}</h1>
          <p className='text-sm text-slate-500'>{t('shipments.subtitle')}</p>
        </div>
        {!creating && (
          <button onClick={onNew} className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600'>
            {t('shipments.new')}
          </button>
        )}
      </div>

      <TrialBanner usage={usage} />
      <UpgradeDialog open={limitOpen} onClose={closeLimit} limit={usage?.limit} />

      {creating && (
        <NewShipmentForm onCancel={() => setCreating(false)} onLimitReached={onLimitReached}
          onCreated={(s) => navigate(`/shipments/${s.shipmentId}`)} />
      )}

      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          {STATUS_FILTERS.map((f) => (
            <button key={f} onClick={() => changeStatus(f)}
              className={`rounded-full px-4 py-1 text-sm font-medium ${status === f ? 'bg-ink text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
              {f ? t(`status.${f}`) : t('common.all')}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => changeQ(e.target.value)} placeholder={t('shipments.search')} className={inputClass} />
      </div>

      <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              <th className='px-4 py-3'>{t('common.status')}</th>
              <th className='px-4 py-3'>{t('shipments.reference')}</th>
              <th className='px-4 py-3'>{t('shipments.incoterm')}</th>
              <th className='px-4 py-3'>{t('shipments.mode')}</th>
              <th className='px-4 py-3 text-center'>{t('shipments.docs')}</th>
              <th className='px-4 py-3 text-center'>{t('shipments.skus')}</th>
              <th className='px-4 py-3 text-center'>{t('shipments.toReview')}</th>
              <th className='px-4 py-3 text-right'>{t('shipments.landedCost')}</th>
              <th className='px-4 py-3'>{t('common.created')}</th>
              <th className='px-4 py-3 text-right'>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {loading && <tr><td colSpan={10} className='px-4 py-8 text-center text-slate-400'>{t('common.loading')}</td></tr>}
            {!loading && shipments.length === 0 && <tr><td colSpan={10} className='px-4 py-8 text-center text-slate-400'>{t('shipments.empty')}</td></tr>}
            {!loading && shipments.map((s) => (
              <tr key={s.shipmentId} className='hover:bg-slate-50'>
                <td className='px-4 py-3'><StatusBadge status={s.status} /></td>
                <td className='px-4 py-3 font-medium'>
                  <Link to={`/shipments/${s.shipmentId}`} className='hover:underline'>{s.reference}</Link>
                </td>
                <td className='px-4 py-3 text-slate-600'>{s.incoterm || '—'}</td>
                <td className='px-4 py-3 text-slate-600'>{t(`modes.${s.mode}`)}</td>
                <td className='px-4 py-3 text-center text-slate-600'>{s.documentsCount}</td>
                <td className='px-4 py-3 text-center text-slate-600'>{s.itemsCount}</td>
                <td className={`px-4 py-3 text-center font-semibold ${s.pendingLines ? 'text-status-review' : 'text-slate-400'}`}>{s.pendingLines}</td>
                <td className='px-4 py-3 text-right tabular-nums text-slate-700'>{fmt.money(s.landedTotal, s.reportCurrency)}</td>
                <td className='px-4 py-3 text-slate-600'>{fmt.date(s.createdAt)}</td>
                <td className='px-4 py-3'>
                  <div className='flex items-center justify-end gap-3'>
                    <Link to={`/shipments/${s.shipmentId}`} className='font-semibold text-brand hover:underline'>{t('common.open')}</Link>
                    <button onClick={() => onDelete(s)} className='font-semibold text-status-incomplete hover:underline'>{t('common.delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='flex flex-wrap items-center justify-between gap-3 text-sm'>
        <div className='flex items-center gap-2 text-slate-600'>
          <span>{t('common.rowsPerPage')}</span>
          <select value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))}
            className='rounded-lg border border-slate-300 px-2 py-1 focus:border-brand focus:outline-none'>
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className='ml-2'>{t('common.totalCount', { count: total })}</span>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className='rounded-lg border border-slate-300 px-3 py-1 font-semibold disabled:opacity-40'>{t('common.prev')}</button>
          <span className='text-slate-600'>{t('common.pageOf', { page, pages: totalPages })}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className='rounded-lg border border-slate-300 px-3 py-1 font-semibold disabled:opacity-40'>{t('common.next')}</button>
        </div>
      </div>
    </div>
  )
}

export default ShipmentsList
